// Mindful Matters — Spiritual redesign content

export const brand = {
  name: "Mindful Matters",
  submark: "Mindful Matters Counseling & Coaching",
  founder: "Devona Nicole Hazelwood",
  credentials: "LPC · NCC · Reiki Master · Author · Transformational Coach",
  tagline:
    "Heal Your Mind. Elevate Your Spirit. Transform Your Life.",
  email: "mindfulmatters369@gmail.com",
  instagram: "https://instagram.com/healing_innergy",
  tiktok: "https://tiktok.com/@healing_inergy",
  profileImage:
    "https://assets.stanwith.me/live/msc/28780813/2ktez/profile1774048829394.jpeg/webp/433x0/100/profile1774048829394.webp",
  logo: "/assets/mindful-matters-logo.jpg",
  stanUrl: "https://stan.store/mindfulmatterscoaching",
  calendlyUrl: "https://calendly.com/mindfulmatters369/30min",
};

export const navLinks = [
  { label: "Philosophy", href: "#philosophy" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Shop", href: "#shop" },
  { label: "Insights", href: "#insights" },
  { label: "Contact", href: "#contact" },
];

export const heroCopy = {
  eyebrow: "Spiritual · Psychological · Somatic",
  headline: [
    { text: "Heal your ", tone: "light" },
    { text: "mind.", tone: "gold" },
  ],
  headline2: [
    { text: "Elevate your ", tone: "light" },
    { text: "spirit.", tone: "gold" },
  ],
  headline3: [
    { text: "Transform your ", tone: "light" },
    { text: "life.", tone: "gold" },
  ],
  body:
    "You don't have to choose between psychology and spirituality. Real healing happens when science meets the soul. A sacred space to move beyond survival — and into alignment.",
};

export const philosophy = {
  eyebrow: "Signature Philosophy",
  lines: [
    { text: "Psychology teaches us", accent: "why we hurt." },
    { text: "Spirituality reminds us", accent: "who we are." },
    { text: "Healing happens", accent: "where they meet." },
  ],
};

export const homepagePositioning = [
  "At Mindful Matters Counseling & Coaching, I help people move beyond survival and into alignment by blending evidence-based psychology with spiritual wisdom, nervous-system regulation, mindfulness, intuitive guidance, and transformational coaching.",
  "Whether you're navigating burnout, anxiety, trauma, life transitions, emotional exhaustion, or simply feeling disconnected from yourself — our work goes deeper than symptom management.",
  "Together we uncover the beliefs, emotional patterns, energetic blocks, and nervous-system responses keeping you stuck, so you can create lasting change from the inside out.",
  "Because healing isn't just about feeling better. It's about becoming who you were always meant to be.",
];

export const aboutCopy = {
  eyebrow: "About Devona",
  title: "Heal deeper. Live lighter.",
  intro:
    "For years I've watched incredible people spend their lives succeeding on the outside while quietly struggling on the inside. They've built careers. Raised families. Supported everyone else. Yet beneath the surface they feel disconnected, overwhelmed, emotionally exhausted, or uncertain about who they are becoming.",
  paragraphs: [
    "I know healing requires more than simply talking about problems. It requires reconnecting with your mind, body, spirit, and nervous system.",
    "My work blends traditional counseling, trauma-informed psychology, nervous-system regulation, mindfulness, spiritual psychology, energy healing, intuitive insight, and transformational coaching into one deeply personalized experience.",
    "I don't believe you are broken. I believe you've adapted. Together we'll identify the patterns that no longer serve you, strengthen your emotional resilience, reconnect you with your inner wisdom, and help you create a life rooted in peace, purpose, authenticity, and alignment.",
  ],
  closing:
    "Because healing isn't about becoming someone new. It's about coming home to yourself.",
};

export const modalities = [
  "Trauma-Informed Psychology",
  "Nervous-System Regulation",
  "Mindfulness & Meditation",
  "Reiki & Energy Healing",
  "Spiritual Psychology",
  "Intuitive Guidance",
  "Somatic Practices",
  "Transformational Coaching",
];

export const services = [
  {
    id: "healing-journey",
    stripeLookupKey: "svc_healing_journey",
    number: "I",
    kind: "Coaching Session",
    title: "Healing Journey Session",
    subtitle:
      "For clients wanting a coaching environment that embraces spirituality.",
    duration: "60 minutes",
    price: "$75",
    perfectFor: [
      "Anxiety",
      "Depression",
      "Trauma",
      "Burnout",
      "Grief",
      "Relationship struggles",
      "Life transitions",
      "Stress",
      "Emotional regulation",
    ],
    cta: "Book a session",
  },
  {
    id: "transformational-coaching",
    stripeLookupKey: "svc_transformational_coaching",
    number: "II",
    kind: "Future-Focused Coaching",
    title: "Transformational Coaching",
    subtitle:
      "Not therapy. Future-focused coaching designed to help you create the life you truly want.",
    duration: "60 minutes",
    price: "$75",
    perfectFor: [
      "Self-worth",
      "Confidence",
      "Boundaries",
      "Manifestation through aligned action",
      "Purpose",
      "Mindset",
      "Leadership",
      "Emotional mastery",
      "Personal growth",
      "Life redesign",
    ],
    cta: "Begin your redesign",
  },
  {
    id: "spiritual-alignment",
    stripeLookupKey: "svc_spiritual_alignment",
    number: "III",
    kind: "Signature Offering",
    title: "Spiritual Alignment Sessions",
    subtitle:
      "A soul-level experience combining energy work, intuitive coaching and nervous-system healing.",
    duration: "90 minutes",
    price: "$125",
    perfectFor: [
      "Energy assessment",
      "Intuitive coaching",
      "Nervous-system regulation",
      "Reiki",
      "Guided meditation",
      "Breathwork",
      "Emotional clearing",
      "Spiritual practices",
      "Personalized action plan",
    ],
    featured: true,
    cta: "Align your energy",
  },
  {
    id: "nervous-system-reset",
    stripeLookupKey: "svc_nervous_system_reset",
    number: "IV",
    kind: "Premium Intensive",
    title: "Nervous System Reset Intensive",
    subtitle:
      "A private three-hour experience to interrupt burnout patterns and rebuild your baseline.",
    duration: "3 hours · private",
    price: "$200",
    perfectFor: [
      "Trauma pattern mapping",
      "Burnout assessment",
      "Emotional regulation tools",
      "Personalized healing roadmap",
      "Meditation recordings",
      "Digital workbook",
      "Follow-up integration session",
    ],
    cta: "Reserve your intensive",
  },
  {
    id: "reiki-session",
    stripeLookupKey: "svc_reiki",
    number: "V",
    kind: "Energy Healing",
    title: "Reiki Session",
    subtitle:
      "Gentle Japanese energy healing held by a Reiki Master — to soothe the nervous system and clear what your mind can't name.",
    duration: "60 minutes",
    price: "$70",
    perfectFor: [
      "Anxiety & stress relief",
      "Energetic clearing",
      "Chakra rebalancing",
      "Deep relaxation",
      "Grief & emotional release",
      "Physical tension",
      "Sleep support",
      "Post-therapy integration",
      "Reconnection to source",
    ],
    cta: "Reserve your session",
  },
];

export const products = [
  {
    id: "mini-guide",
    category: "personal",
    tag: "Free download",
    title: "She Chose Herself — 7-Day Reset",
    price: "Free",
    priceNote: "Instant download",
    description:
      "A 7-day mindfulness-based reset for the woman who carries it all. Daily practices to help you release over-responsibility, restore your energy, and return to yourself.",
    image:
      "https://assets.stanwith.me/live/msc/28780813/mkjqy/product1774044182696.jpeg/webp/433x433/100/product1774044182696.webp",
    button: "Get your copy",
    href: "https://stan.store/mindfulmatterscoaching",
  },
  {
    id: "vibrate-higher",
    category: "personal",
    tag: "The Book",
    title: "Vibrate Higher",
    subtitle: "A 30-Day Pathway to Awaken, Align, and Ascend into Higher Consciousness.",
    price: "$11.11",
    priceNote: "Available on Amazon",
    description:
      "Devona's flagship book — a spiritual and psychological roadmap for ascension, alignment, and emotional freedom. Weekly practices, journal prompts, and channeled wisdom for the awakening soul.",
    image: "/assets/vibrate-higher-cover.jpg",
    button: "Get the book",
    href: "https://a.co/d/00p4nPys",
    featured: true,
  },
  {
    id: "rise-regardless",
    category: "personal",
    tag: "Best-selling guide",
    title: "Rise Regardless",
    subtitle:
      "A Woman's Complete Blueprint for Thriving When the Workplace Works Against You.",
    price: "$11.99",
    priceNote: "Available on Amazon",
    description:
      "Emotional resilience, practical strategies, and legal-rights literacy for women navigating toxic work environments. Written from a therapist's chair.",
    image:
      "https://assets.stanwith.me/live/msc/28780813/murvn/product1774044288914.jpeg/webp/433x433/100/product1774044288914.webp",
    button: "Get the book",
    href: "https://a.co/d/0eZr5fnh",
  },
  {
    id: "when-students-hurt",
    category: "educator",
    tag: "Book One",
    title: "When Students Hurt",
    subtitle: "A Guide for Urban Educators.",
    price: "$17.99",
    priceNote: "Available on Amazon",
    description:
      "A plain-language, practical field guide for every adult working with students in urban schools. Breaks down trauma, behavior, and healing into simple concepts and actionable strategies for the classroom, counseling office, or main office.",
    image: "/assets/when-students-hurt-cover.jpg",
    button: "Get the book",
    href: "https://a.co/d/07ems2di",
    featured: true,
  },
  {
    id: "when-helping-hurts",
    category: "educator",
    tag: "Book Two",
    title: "When Helping Hurts",
    subtitle: "Why educators cannot effectively incorporate SEL strategies without first addressing their own trauma.",
    price: "$17.99",
    priceNote: "Available on Amazon",
    description:
      "The second book in the Trauma-Informed Schools Series — for educators, administrators, and school staff. A field guide for the helpers running on empty, because students can't heal from an empty bucket.",
    image: "/assets/when-helping-hurts-cover.jpg",
    button: "Get the book",
    href: "https://a.co/d/0iTV5DsQ",
  },
];

export const shopTabs = [
  { id: "personal", label: "Personal Growth" },
  { id: "educator", label: "Educator Resources" },
];

export const testimonials = [
  {
    quote:
      "Devona doesn't just do therapy — she does soul work. I walked in exhausted and walked out remembering who I actually am.",
    name: "Jasmine R.",
    role: "3rd-grade teacher",
  },
  {
    quote:
      "The Spiritual Alignment session shifted something in my body I couldn't reach in years of talk therapy. Sacred, tender, transformative.",
    name: "Alicia M.",
    role: "ICU nurse & mom of two",
  },
  {
    quote:
      "Vibrate Higher is the book my nervous system was waiting for. It reads like a friend and hits like a lightning rod.",
    name: "Kendra B.",
    role: "Stay-at-home mom",
  },
  {
    quote:
      "She is a licensed clinician AND an energy worker. That combination is rare, and it is the reason my healing finally landed.",
    name: "Sade O.",
    role: "Small-business entrepreneur",
  },
];

export const insights = [
  {
    id: "nervous-system",
    date: "Waxing Crescent · 2025",
    readTime: "6 min read",
    category: "Nervous System",
    title: "Your body is not the problem — it is the messenger.",
    excerpt:
      "Anxiety is often the language of a nervous system that was never given the safety to soften. Here's what your symptoms may actually be trying to say.",
    image:
      "https://images.unsplash.com/photo-1669813013286-9d9be72171ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxldGhlcmVhbCUyMGxpZ2h0fGVufDB8fHx8MTc4NTcwNjg1OHww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "ascension",
    date: "Full Moon · 2025",
    readTime: "7 min read",
    category: "Ascension",
    title: "Ascension is not a bypass. It is a homecoming.",
    excerpt:
      "Spiritual growth without emotional healing is a spiritual bypass. Real ascension asks us to feel everything we abandoned in order to survive.",
    image:
      "https://images.unsplash.com/photo-1503416997304-7f8bf166c121?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxtb29uJTIwY2VsZXN0aWFsfGVufDB8fHx8MTc4NTcwNjg1M3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "vibrate",
    date: "New Moon · 2025",
    readTime: "5 min read",
    category: "Energy",
    title: "How to vibrate higher when the world feels heavy.",
    excerpt:
      "Frequency is not performance. It is protection. A therapist-and-Reiki-Master's take on raising your vibration without abandoning your humanity.",
    image:
      "https://images.unsplash.com/photo-1531874993088-51b60dda4452?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxldGhlcmVhbCUyMGxpZ2h0fGVufDB8fHx8MTc4NTcwNjg1OHww&ixlib=rb-4.1.0&q=85",
  },
];

export const faqs = [
  {
    q: "Is this therapy, coaching, or spiritual work?",
    a: "It can be any of the three, depending on what you need. Spiritual Counseling is licensed therapy that honors your spirituality. Transformational Coaching is future-focused and outside a therapy frame. Spiritual Alignment blends energy work, coaching and somatic care. We'll match the container to the moment you're in.",
  },
  {
    q: "Do I have to be spiritual to work with you?",
    a: "Not at all. My clients range from lifelong seekers to people who are just beginning to trust that there is more. The work is trauma-informed and evidence-based first — spirituality is invited, never imposed.",
  },
  {
    q: "Where do sessions happen?",
    a: "All sessions are held virtually via a secure video platform, so you can meet me from wherever you feel safest.",
  },
  {
    q: "Do you accept insurance?",
    a: "I am an out-of-network provider. Superbills are available on request for possible reimbursement, and every offering includes an interest-free two- or three-payment option.",
  },
  {
    q: "What is Reiki, exactly?",
    a: "Reiki is a gentle Japanese energy-healing modality that supports the nervous system, emotional body, and subtle-energy field. I hold a Reiki Master certification and integrate it into Spiritual Alignment sessions when it is a fit.",
  },
];

export const heroImages = {
  nebula1:
    "https://images.unsplash.com/photo-1679615845580-8691c78fd7d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxjb3NtaWMlMjBuZWJ1bGF8ZW58MHx8fHwxNzg1NzA2ODUzfDA&ixlib=rb-4.1.0&q=85",
  nebula2:
    "https://images.unsplash.com/photo-1706800696671-570820e7ff39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjb3NtaWMlMjBuZWJ1bGF8ZW58MHx8fHwxNzg1NzA2ODUzfDA&ixlib=rb-4.1.0&q=85",
  nebula3:
    "https://images.unsplash.com/photo-1708112292872-fd5612affa79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxjb3NtaWMlMjBuZWJ1bGF8ZW58MHx8fHwxNzg1NzA2ODUzfDA&ixlib=rb-4.1.0&q=85",
  moon:
    "https://images.unsplash.com/photo-1503416997304-7f8bf166c121?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxtb29uJTIwY2VsZXN0aWFsfGVufDB8fHx8MTc4NTcwNjg1M3ww&ixlib=rb-4.1.0&q=85",
  aurora:
    "https://images.unsplash.com/photo-1499578124509-1611b77778c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtb29uJTIwY2VsZXN0aWFsfGVufDB8fHx8MTc4NTcwNjg1M3ww&ixlib=rb-4.1.0&q=85",
  light1:
    "https://images.unsplash.com/photo-1531874993088-51b60dda4452?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxldGhlcmVhbCUyMGxpZ2h0fGVufDB8fHx8MTc4NTcwNjg1OHww&ixlib=rb-4.1.0&q=85",
  light2:
    "https://images.unsplash.com/photo-1669813013286-9d9be72171ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxldGhlcmVhbCUyMGxpZ2h0fGVufDB8fHx8MTc4NTcwNjg1OHww&ixlib=rb-4.1.0&q=85",
  chakra:
    "https://images.pexels.com/photos/36005360/pexels-photo-36005360.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};
